import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, Moon, Search, Sun, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/store/authStore';
import { usePublicSearchStore } from '@/store/publicSearchStore';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

const publicNavItems = [
  { label: 'Courses', path: '/courses' },
  { label: 'Sports', path: '/sports' },
  { label: 'Events', path: '/events' },
];

const headerThirukkural = {
  firstLine: 'கேடில் விழுச்செல்வம் கல்வி யொருவற்கு',
  secondLine: 'மாடல்ல மற்றை யவை.',
};

const publicSearchConfigs = [
  {
    path: '/courses',
    scope: 'courses',
    label: 'courses',
    placeholder: 'Search courses by title',
  },
  {
    path: '/sports',
    scope: 'sports',
    label: 'sports academies',
    placeholder: 'Search sports academies by title',
  },
  {
    path: '/events',
    scope: 'events',
    label: 'events and workshops',
    placeholder: 'Search events and workshops by title',
  },
] as const;

const mobileMenuAnimationMs = 260;
const themeStorageKey = 'genkalvi-theme';

const getPublicSearchConfig = (pathname: string) =>
  publicSearchConfigs.find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`)) ??
  publicSearchConfigs[0];

interface PublicSearchFormProps {
  className?: string;
  id: string;
  compact?: boolean;
  onSubmitComplete?: () => void;
}

const PublicSearchForm = ({ className = '', id, compact = false, onSubmitComplete }: PublicSearchFormProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const publicSearchConfig = getPublicSearchConfig(location.pathname);
  const searchQuery = usePublicSearchStore((state) => state.queries[publicSearchConfig.scope]);
  const setSearchQuery = usePublicSearchStore((state) => state.setQuery);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchQuery(publicSearchConfig.scope, params.get('search') || '');
  }, [location.search, publicSearchConfig.scope, setSearchQuery]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(publicSearchConfig.scope, event.target.value);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchQuery.trim();
    onSubmitComplete?.();
    navigate(query ? `${publicSearchConfig.path}?search=${encodeURIComponent(query)}` : publicSearchConfig.path);
  };

  return (
    <form onSubmit={submitSearch} className={className}>
      <label className="sr-only" htmlFor={id}>
        Search {publicSearchConfig.label}
      </label>
      <div className="public-search-shell group relative">
        <span
          className={cn(
            'public-search-icon pointer-events-none absolute top-1/2 z-10 inline-flex -translate-y-1/2 items-center justify-center rounded-full',
            compact ? 'left-1 h-7 w-7' : 'left-1.5 h-8 w-8'
          )}
        >
          <Search className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        </span>
        <input
          id={id}
          value={searchQuery}
          onChange={handleChange}
          placeholder={publicSearchConfig.placeholder}
          className={cn(
            'public-search-input w-full rounded-full border border-slate-300 bg-white text-slate-900 shadow-none outline-none transition-colors duration-200 placeholder:text-slate-400 focus:border-slate-300 focus:ring-0',
            compact ? 'h-9 pl-10 pr-3 text-xs' : 'h-11 pl-12 pr-5 text-sm'
          )}
        />
      </div>
    </form>
  );
};

export const Header = () => {
  const { isAuthenticated, user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shouldRenderMobileMenu, setShouldRenderMobileMenu] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(themeStorageKey) === 'dark';
  });

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.documentElement.classList.toggle('theme-dark', isDarkTheme);
    window.localStorage.setItem(themeStorageKey, isDarkTheme ? 'dark' : 'light');
  }, [isDarkTheme]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setShouldRenderMobileMenu(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRenderMobileMenu(false);
    }, mobileMenuAnimationMs);

    return () => window.clearTimeout(timeoutId);
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const destination = user?.role === 'admin' ? '/admin' : '/tutor';
  const isPublicNavActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const showPublicNav = !isAuthenticated && location.pathname !== '/login';

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className="site-header fixed inset-x-0 top-0 z-50 h-16 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="relative mx-auto h-16 max-w-[90rem] px-4 sm:px-6 lg:px-8">
        <div className="flex h-full items-center gap-4">
          <Link to="/" className="site-brand flex min-w-0 shrink-0 items-center gap-3 md:min-w-[13rem]">
            <img src="/genkalvi-logo.svg" alt="Genkalvi Academy logo" className="h-11 w-11 object-contain" />
            <span className="max-w-[11rem] truncate text-sm font-semibold tracking-tight text-slate-950 sm:max-w-none sm:text-base md:text-lg">
              Genkalvi Academy
            </span>
          </Link>

          {showPublicNav ? (
            <>
              <nav aria-label="Primary" className="site-nav-group ml-2 hidden shrink-0 items-center gap-0.5 md:flex">
                {publicNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'site-nav-link site-nav-link-animated relative inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg px-3.5 py-1 text-[0.82rem] font-semibold transition-colors duration-200',
                      isPublicNavActive(item.path)
                        ? 'site-nav-link-active text-white'
                        : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-950'
                    )}
                  >
                    {isPublicNavActive(item.path) ? (
                      <motion.span
                        layoutId="public-nav-active-tab"
                        className="site-nav-active-indicator absolute inset-0 rounded-lg"
                        transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                      />
                    ) : null}
                    <span className="relative z-10">{item.label}</span>
                  </Link>
                ))}
              </nav>

            </>
          ) : (
            <div className="flex-1" />
          )}

          {showPublicNav ? (
            <blockquote className="site-header-kural pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 xl:flex" lang="ta">
              <span>{headerThirukkural.firstLine}</span>
              <span>{headerThirukkural.secondLine}</span>
            </blockquote>
          ) : null}

          {isAuthenticated ? (
            <>
              <div className="ml-auto hidden shrink-0 items-center gap-2 md:flex">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(destination)}
                  className="site-auth-button"
                >
                  {user?.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="site-auth-button site-auth-button-outline"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            </>
          ) : null}

          {showPublicNav ? (
            <PublicSearchForm
              id="site-search-desktop"
              compact
              className="site-desktop-search ml-auto hidden w-48 shrink-0 md:block lg:w-56 xl:w-64"
            />
          ) : null}

          <button
            type="button"
            onClick={() => setIsDarkTheme((value) => !value)}
            className="theme-toggle ml-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 md:ml-0"
            aria-label={isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={isDarkTheme}
            title={isDarkTheme ? 'Light mode' : 'Dark mode'}
          >
            {isDarkTheme ? (
              <Sun className="h-5 w-5 text-amber-500" strokeWidth={2.2} />
            ) : (
              <Moon className="h-5 w-5 text-slate-800" strokeWidth={2.2} />
            )}
          </button>

          {(isAuthenticated || showPublicNav) ? (
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className={cn(
                'site-menu-button inline-flex h-10 w-10 items-center justify-center rounded-lg border text-slate-700 transition-all duration-300 md:hidden',
                isMobileMenuOpen
                  ? 'border-slate-950 bg-slate-950 text-white'
                  : 'border-slate-200 bg-white'
              )}
              aria-controls="site-mobile-menu"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 transition-transform duration-300 motion-reduce:transition-none" />
              ) : (
                <Menu className="h-5 w-5 transition-transform duration-300 motion-reduce:transition-none" />
              )}
            </button>
          ) : null}
        </div>

        {shouldRenderMobileMenu && (isAuthenticated || showPublicNav) && (
          <div
            id="site-mobile-menu"
            aria-hidden={!isMobileMenuOpen}
            className={cn(
              'site-mobile-menu absolute left-0 right-0 top-full grid overflow-hidden border-b border-slate-200 bg-white transition-[grid-template-rows,opacity] duration-300 ease-out md:hidden motion-reduce:transition-none',
              isMobileMenuOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            )}
          >
            <div className="min-h-0 max-h-[calc(100dvh-var(--app-header-height))] overflow-y-auto">
              <div
                className={cn(
                  'space-y-4 px-4 py-4 transition-all duration-300 ease-out motion-reduce:transition-none',
                  isMobileMenuOpen ? 'translate-y-0' : '-translate-y-3'
                )}
              >
                {isAuthenticated ? (
                  <>
                    <div
                      className={cn(
                        'space-y-3 transition-all duration-300 ease-out delay-75 motion-reduce:transition-none',
                        isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                      )}
                    >
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(destination)}
                          className="site-auth-button"
                        >
                          {user?.role === 'admin' ? 'Admin Panel' : 'Dashboard'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleLogout}
                          className="site-auth-button site-auth-button-outline"
                        >
                          Logout
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={cn(
                        'transition-all duration-300 ease-out motion-reduce:transition-none',
                        isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                      )}
                    >
                      <PublicSearchForm id="site-search-mobile" onSubmitComplete={closeMobileMenu} />
                    </div>

                    <nav aria-label="Mobile primary" className="grid gap-2">
                      {publicNavItems.map((item, index) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={closeMobileMenu}
                          style={{ transitionDelay: isMobileMenuOpen ? `${index * 45}ms` : '0ms' }}
                          className={cn(
                            'site-nav-link inline-flex min-h-9 w-full items-center rounded-lg px-3 py-1.5 text-sm font-semibold transition-all duration-300 ease-out motion-reduce:transition-none',
                            isPublicNavActive(item.path)
                              ? 'site-nav-link-active bg-slate-950 text-white'
                              : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950',
                            isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-3 opacity-0'
                          )}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </nav>

                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

    </header>
  );
};
